import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { 
  Truck, 
  Package, 
  FileText, 
  ClipboardCheck, 
  ShoppingCart,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Clock,
  Users,
  BarChart3,
  Loader2,
  RefreshCw,
  Shield,
  Activity,
  DollarSign,
  Calendar,
  Eye
} from 'lucide-react';

const GerenteDashboard = () => {
  const { user, hasPermission, getEmpresaId } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentActivities, setRecentActivities] = useState([]);
  const [error, setError] = useState('');
  const [lastUpdate, setLastUpdate] = useState(null);

  useEffect(() => {
    if (hasPermission('view_dashboard')) {
      loadDashboardData();
    }
  }, [hasPermission]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('🔍 GerenteDashboard: Carregando dados da empresa...');

      const empresaId = getEmpresaId();

      // Carregar estatísticas específicas da empresa em paralelo
      const [
        suppliersResult,
        productsResult,
        documentsResult,
        marketplaceResult,
        receivingsResult,
        usersResult
      ] = await Promise.allSettled([
        httpsCallable(functions, 'getSupplierStats')(),
        httpsCallable(functions, 'getProductStats')(),
        httpsCallable(functions, 'getDocumentStats')(),
        httpsCallable(functions, 'getMarketplaceStats')(),
        httpsCallable(functions, 'getReceivingStats')({ empresa_id: empresaId }),
        httpsCallable(functions, 'listUsers')({ page: 1, limit: 1000, id_empresa: empresaId })
      ]);

      // Processar resultados específicos da empresa
      const companyStats = {
        // Fornecedores
        total_fornecedores: suppliersResult.status === 'fulfilled' ? 
          suppliersResult.value.data?.data?.total || 0 : 0,
        fornecedores_ativos: suppliersResult.status === 'fulfilled' ? 
          suppliersResult.value.data?.data?.por_status?.aprovado || 0 : 0,
        fornecedores_pendentes: suppliersResult.status === 'fulfilled' ? 
          suppliersResult.value.data?.data?.por_status?.pendente || 0 : 0,

        // Produtos
        total_produtos: productsResult.status === 'fulfilled' ? 
          productsResult.value.data?.data?.total || 0 : 0,
        produtos_ativos: productsResult.status === 'fulfilled' ? 
          productsResult.value.data?.data?.por_status?.ativo || 0 : 0,
        produtos_baixo_estoque: productsResult.status === 'fulfilled' ? 
          productsResult.value.data?.data?.baixo_estoque || 0 : 0,

        // Documentos
        documentos_pendentes: documentsResult.status === 'fulfilled' ? 
          documentsResult.value.data?.data?.por_status?.pendente || 0 : 0,
        documentos_aprovados: documentsResult.status === 'fulfilled' ? 
          documentsResult.value.data?.data?.por_status?.aprovado || 0 : 0,
        documentos_vencendo: documentsResult.status === 'fulfilled' ? 
          documentsResult.value.data?.data?.vencendo_30_dias || 0 : 0,

        // Recebimentos (getReceivingStats: total, por_decisao, por_conformidade, quantidades)
        recebimentos_mes: receivingsResult.status === 'fulfilled' ? 
          receivingsResult.value.data?.data?.total || 0 : 0,
        recebimentos_pendentes: receivingsResult.status === 'fulfilled' ? 
          receivingsResult.value.data?.data?.por_decisao?.pendente || 0 : 0,
        valor_recebimentos_mes: receivingsResult.status === 'fulfilled' ? 
          receivingsResult.value.data?.data?.quantidade_aceita || 0 : 0,

        // Marketplace
        propostas_marketplace: marketplaceResult.status === 'fulfilled' ? 
          marketplaceResult.value.data?.data?.total || 0 : 0,
        propostas_ativas: marketplaceResult.status === 'fulfilled' ? 
          marketplaceResult.value.data?.data?.por_status?.ativa || 0 : 0,

        // Usuários
        usuarios_empresa: usersResult.status === 'fulfilled' ? 
          usersResult.value.data?.data?.length || 0 : 0
      };

      setStats(companyStats);
      setLastUpdate(new Date());

      // Simular atividades recentes baseadas nos dados reais
      const activities = generateRecentActivities(companyStats);
      setRecentActivities(activities);

      console.log('✅ GerenteDashboard: Dados carregados com sucesso', companyStats);

    } catch (error) {
      console.error('❌ GerenteDashboard: Erro ao carregar dados:', error);
      setError('Erro ao carregar dados do dashboard. Tente novamente.');
      
      // Fallback para dados básicos em caso de erro
      setStats({
        total_fornecedores: 0,
        fornecedores_ativos: 0,
        fornecedores_pendentes: 0,
        total_produtos: 0,
        produtos_ativos: 0,
        produtos_baixo_estoque: 0,
        documentos_pendentes: 0,
        documentos_aprovados: 0,
        documentos_vencendo: 0,
        recebimentos_mes: 0,
        recebimentos_pendentes: 0,
        valor_recebimentos_mes: 0,
        propostas_marketplace: 0,
        propostas_ativas: 0,
        usuarios_empresa: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const generateRecentActivities = (stats) => {
    const activities = [];
    const now = new Date();

    if (stats.fornecedores_pendentes > 0) {
      activities.push({
        id: 1,
        tipo: 'fornecedor_pendente',
        descricao: `${stats.fornecedores_pendentes} fornecedor(es) aguardando aprovação`,
        usuario: 'sistema',
        data: new Date(now.getTime() - 1000 * 60 * 30),
        status: 'warning'
      });
    }

    if (stats.documentos_vencendo > 0) {
      activities.push({
        id: 2,
        tipo: 'documento_vencendo',
        descricao: `${stats.documentos_vencendo} documento(s) vencendo em 30 dias`,
        usuario: 'sistema',
        data: new Date(now.getTime() - 1000 * 60 * 60),
        status: 'warning'
      });
    }

    if (stats.recebimentos_pendentes > 0) {
      activities.push({
        id: 3,
        tipo: 'recebimento_pendente',
        descricao: `${stats.recebimentos_pendentes} recebimento(s) aguardando validação`,
        usuario: 'sistema',
        data: new Date(now.getTime() - 1000 * 60 * 45),
        status: 'info'
      });
    }

    if (stats.produtos_baixo_estoque > 0) {
      activities.push({
        id: 4,
        tipo: 'estoque_baixo',
        descricao: `${stats.produtos_baixo_estoque} produto(s) com estoque baixo`,
        usuario: 'sistema',
        data: new Date(now.getTime() - 1000 * 60 * 90),
        status: 'warning'
      });
    }

    if (stats.propostas_ativas > 0) {
      activities.push({
        id: 5,
        tipo: 'marketplace_ativo',
        descricao: `${stats.propostas_ativas} proposta(s) ativa(s) no marketplace`,
        usuario: 'sistema',
        data: new Date(now.getTime() - 1000 * 60 * 120),
        status: 'success'
      });
    }

    return activities.slice(0, 5); // Máximo 5 atividades
  };

  const handleRefresh = () => {
    loadDashboardData();
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getActivityIcon = (tipo) => {
    switch (tipo) {
      case 'fornecedor_pendente':
        return <Truck className="h-4 w-4" />;
      case 'documento_vencendo':
        return <FileText className="h-4 w-4" />;
      case 'recebimento_pendente':
        return <ClipboardCheck className="h-4 w-4" />;
      case 'estoque_baixo':
        return <Package className="h-4 w-4" />;
      case 'marketplace_ativo':
        return <ShoppingCart className="h-4 w-4" />;
      default:
        return <Activity className="h-4 w-4" />;
    }
  };

  const getActivityColor = (status) => {
    switch (status) {
      case 'success':
        return 'text-green-600 bg-green-100';
      case 'warning':
        return 'text-yellow-600 bg-yellow-100';
      case 'error':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-blue-600 bg-blue-100';
    }
  };

  if (!hasPermission('view_dashboard')) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Acesso Restrito</h3>
          <p className="text-gray-500">Você não tem permissão para visualizar o dashboard.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Carregando dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard Gerencial</h1>
          <p className="text-gray-600">
            Visão geral das operações da empresa
          </p>
        </div>
        <div className="flex items-center space-x-4">
          {lastUpdate && (
            <span className="text-sm text-gray-500">
              Última atualização: {formatDate(lastUpdate)}
            </span>
          )}
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-red-600 mr-2" />
            <span className="text-red-800">{error}</span>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Fornecedores */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Fornecedores</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.total_fornecedores || 0}</p>
              <p className="text-sm text-green-600">
                {stats?.fornecedores_ativos || 0} ativos
              </p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <Truck className="h-6 w-6 text-blue-600" />
            </div>
          </div>
          {stats?.fornecedores_pendentes > 0 && (
            <div className="mt-2 text-sm text-yellow-600">
              {stats.fornecedores_pendentes} pendente(s)
            </div>
          )}
        </div>

        {/* Produtos */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Ingredientes</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.total_produtos || 0}</p>
              <p className="text-sm text-green-600">
                {stats?.produtos_ativos || 0} ativos
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <Package className="h-6 w-6 text-green-600" />
            </div>
          </div>
          {stats?.produtos_baixo_estoque > 0 && (
            <div className="mt-2 text-sm text-yellow-600">
              {stats.produtos_baixo_estoque} com estoque baixo
            </div>
          )}
        </div>

        {/* Documentos */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Documentos</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.documentos_aprovados || 0}</p>
              <p className="text-sm text-green-600">aprovados</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-full">
              <FileText className="h-6 w-6 text-purple-600" />
            </div>
          </div>
          {stats?.documentos_pendentes > 0 && (
            <div className="mt-2 text-sm text-yellow-600">
              {stats.documentos_pendentes} pendente(s)
            </div>
          )}
          {stats?.documentos_vencendo > 0 && (
            <div className="mt-1 text-sm text-red-600">
              {stats.documentos_vencendo} vencendo
            </div>
          )}
        </div>

        {/* Recebimentos */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Recebimentos (Mês)</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.recebimentos_mes || 0}</p>
              <p className="text-sm text-green-600">
                {formatCurrency(stats?.valor_recebimentos_mes)}
              </p>
            </div>
            <div className="p-3 bg-orange-100 rounded-full">
              <ClipboardCheck className="h-6 w-6 text-orange-600" />
            </div>
          </div>
          {stats?.recebimentos_pendentes > 0 && (
            <div className="mt-2 text-sm text-yellow-600">
              {stats.recebimentos_pendentes} pendente(s)
            </div>
          )}
        </div>
      </div>

      {/* Additional Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Marketplace */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Marketplace</h3>
            <ShoppingCart className="h-5 w-5 text-gray-400" />
          </div>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Total de propostas:</span>
              <span className="text-sm font-medium">{stats?.propostas_marketplace || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Propostas ativas:</span>
              <span className="text-sm font-medium text-green-600">{stats?.propostas_ativas || 0}</span>
            </div>
          </div>
        </div>

        {/* Usuários */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Equipe</h3>
            <Users className="h-5 w-5 text-gray-400" />
          </div>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Usuários ativos:</span>
              <span className="text-sm font-medium">{stats?.usuarios_empresa || 0}</span>
            </div>
          </div>
        </div>

        {/* Performance */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Performance</h3>
            <BarChart3 className="h-5 w-5 text-gray-400" />
          </div>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Taxa de aprovação:</span>
              <span className="text-sm font-medium text-green-600">
                {stats?.total_fornecedores > 0 
                  ? Math.round((stats.fornecedores_ativos / stats.total_fornecedores) * 100)
                  : 0}%
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Documentos em dia:</span>
              <span className="text-sm font-medium text-green-600">
                {stats?.documentos_aprovados > 0 && stats?.documentos_vencendo >= 0
                  ? Math.round(((stats.documentos_aprovados - stats.documentos_vencendo) / stats.documentos_aprovados) * 100)
                  : 0}%
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activities */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Atividades Recentes</h3>
        </div>
        <div className="p-6">
          {recentActivities.length > 0 ? (
            <div className="space-y-4">
              {recentActivities.map((activity) => (
                <div key={activity.id} className="flex items-start space-x-3">
                  <div className={`p-2 rounded-full ${getActivityColor(activity.status)}`}>
                    {getActivityIcon(activity.tipo)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900">{activity.descricao}</p>
                    <p className="text-xs text-gray-500">
                      {formatDate(activity.data)} • {activity.usuario}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Activity className="h-8 w-8 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-500">Nenhuma atividade recente</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GerenteDashboard;

